import React, { useEffect, useState } from 'react'
import classnames from 'classnames'
import { isEnterOrSpacePressed } from 'utils/keyboardUtils'

import Checkbox from '../../Checkbox/Checkbox'
import Tooltip from '../../Tooltip/Tooltip'
import Button from '../../Button/Button'

import { defaultValue, getRowsLength, setWidth } from '../util'


const IndeterminateCheckbox = React.forwardRef(
  (
    { indeterminate, maxSelect, selectedData = [], rowData = {}, uniqueKey, rowsLength, ...rest }: any,
    ref
  ) => {
    const [disable, setDisable] = useState(false)
    const maxSelectInt = parseInt(maxSelect)
    const defaultRef = React.useRef()
    const resolvedRef: any = ref || defaultRef

    useEffect(() => {
      if (
        selectedData.length >= maxSelectInt &&
        rowData &&
        !selectedData.find((data) => data[uniqueKey] === rowData[uniqueKey]) &&
        !rowData.isHeader
      ) {
        setDisable(true)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indeterminate])

    useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedRef, indeterminate])

    const handleKeyDown = (e) => {
      if (e.target.id === 'rowSelect' && e.key === " ") {
        setTimeout(() => {
          document.getElementById('bulk-panel-action-item-0')?.focus()
        }, 500)
      }
    }

    const handleWrapperClick = (e) => {
      e?.stopPropagation();
      e?.preventDefault();
      resolvedRef.current.click()
    }

    const headerCheckboxCondCheck = rowData.isHeader && !rowsLength

    const tooltipContent = headerCheckboxCondCheck
      ? 'Cannot bulk select any items in the list below'
      : disable || (rowData.isHeader && maxSelectInt === selectedData.length)
        ? (
          rest.bulkSelectionLimitMessage || 'Bulk selection limit reached'
        )
        : ''

    return (
      <div className='checkbox-wrapper' onClick={handleWrapperClick} >
        {
          tooltipContent ? (
            <div className="max-select-tooltip">
              <Tooltip content={tooltipContent} position="bottom">
                <Checkbox
                  inputRef={resolvedRef}
                  {...rest}
                  id="rowSelect"
                  disabled={headerCheckboxCondCheck ? true : disable}
                  onKeyDown={handleKeyDown}
                />
              </Tooltip>
            </div>
          ) : (
            <Checkbox
              inputRef={resolvedRef}
              {...rest}
              id="rowSelect"
              disabled={headerCheckboxCondCheck ? true : disable}
              onKeyDown={handleKeyDown}
            />
          )
        }
      </div>
    )
  }
)

const isTableRowActDisabled = (tableRowActionList, data) => {
  let listStatus = []
  tableRowActionList.forEach((listObj) => {
    const canDisplay = typeof listObj.canDisplay === 'function' ? listObj.canDisplay(data) : true
    listStatus.push(canDisplay)
  })
  //if list has any true that means, have to show table row act
  const truthyCheck = listStatus.includes(true)
  return !truthyCheck
}

export const pushActionColumn = (hooks, { rowActionData, updateRowActionData, tableRowActionList = [] }) => {
  hooks.visibleColumns.push((columns: any) => [
    ...columns,
    {
      id: 'tableActions',
      disableSortBy: true,
      width: 93,
      Header: 'Actions',
      testId: 'cs-table-row-action-column',
      Cell: ({ row }) => {
        const isActDisabled = isTableRowActDisabled(tableRowActionList, row.original)

        return (
          <Button
            className={classnames(
              'three-dots-vertical-icon',
              rowActionData.rowIndex === row.index && 'three-dots-vertical-icon-selected'
            )}
            onlyIconActive={rowActionData.rowIndex === row.index}
            disabled={isActDisabled}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()

              if (isActDisabled) {
                return
              }
              const { target }: any = e
              const parentWithClass = target.closest('div.Table__body__cell--tableRowAction')
              let targetTop = parentWithClass.getBoundingClientRect().top
              let targetLeft = parentWithClass.getBoundingClientRect().left
              if (rowActionData.rowIndex !== row.index) {
                //first close will run on unmount of TableRowAction and then open
                setTimeout(() => {
                  updateRowActionData({
                    rowIndex: row.index,
                    clientY: targetTop,
                    clientX: targetLeft
                  })
                })
              } else {
                setTimeout(() => {
                  updateRowActionData({ rowIndex: -1 });
                })
              }
            }}
            role="menu"
            tabIndex={isActDisabled ? -1 : 0}
            aria-label={`row ${row.index + 1} action`}
            onKeyDown={(e: any) => {
              if (isEnterOrSpacePressed(e.key)) {
                e.target.click()
                setTimeout(() => {
                  const verticalActionList: any = document.querySelector('.VerticalActionTooltip__actions>li')
                  verticalActionList?.focus()
                })
                e.stopPropagation()
                e.preventDefault()
              }
            }}
            size="regular"
            buttonType="secondary"
            onlyIcon={true}
            icon={'v2-DotsThreeLargeVertical'}
            version="v2"
            onlyIconHoverColor='secondary'
            testId="cs-table-action-options"
          />
        )
      }
    }
  ])
}

const pushCheckboxInRow = (
  hooks,
  { rowSelectCheckboxProp, selectedData, maxSelect, uniqueKey, rowDisableProp, bulkSelectionLimitMessage }
) => {
  hooks.visibleColumns.push((columns: any) => [
    {
      id: 'selection',
      disableSortBy: true,
      minWidth: defaultValue.selectColumnWidth,
      width: defaultValue.selectColumnWidth,
      maxWidth: defaultValue.selectColumnWidth,
      testId: 'cs-table-row-selection',
      Header: ({
        getToggleAllRowsSelectedProps,
        toggleRowSelected,
        toggleAllRowsSelected,
        selectedFlatRows,
        rows
      }: any) => {
        //todo refactor this in a func
        let checkboxProps = { ...getToggleAllRowsSelectedProps() }
        const rowsLength = getRowsLength(rows, rowSelectCheckboxProp)
        if (maxSelect) {
          let currentSelectLimit = maxSelect - selectedData.length

          const overriddenChecked =
            selectedData.length &&
            rows.length &&
            (selectedData.length === maxSelect || (rowsLength < maxSelect && selectedFlatRows.length === rowsLength)
              ? true
              : false)
          const overriddenIndeterminate = !overriddenChecked && selectedData.length > 0

          const overriddenOnChange = (e) => {
            if (currentSelectLimit > 0 && e.currentTarget.checked) {
              let count = 0
              for (let i = 0; i < rows.length; i++) {
                let tableRow = rows[i]
                if (count >= currentSelectLimit) {
                  return
                }
                if (rowSelectCheckboxProp) {
                  if (
                    tableRow.original[rowSelectCheckboxProp.key] === rowSelectCheckboxProp.value &&
                    !tableRow.isSelected
                  ) {
                    toggleRowSelected(tableRow.id, e.currentTarget.checked)
                    count = count + 1
                  }
                } else if (!tableRow.isSelected) {
                  count = count + 1
                  toggleRowSelected(tableRow.id, e.currentTarget.checked)
                }
              }
            } else if (!e.currentTarget.checked) {
              toggleAllRowsSelected(e.currentTarget.checked)
            }
          }
          checkboxProps = {
            ...checkboxProps,
            onChange: overriddenOnChange,
            checked: overriddenChecked,
            indeterminate: overriddenIndeterminate
          }
        }
        return (
          <IndeterminateCheckbox
            {...checkboxProps}
            // totalCount={totalCount}
            selectedData={selectedData}
            rowData={{ isHeader: true }}
            maxSelect={maxSelect}
            uniqueKey={uniqueKey}
            rowsLength={rowsLength}
            title="Bulk select Items"
            bulkSelectionLimitMessage={bulkSelectionLimitMessage}
          />
        )
      },
      Cell: ({ row }: any) => {
        if (rowSelectCheckboxProp) {
          if (row.original[rowSelectCheckboxProp.key] === rowSelectCheckboxProp.value) {
            if (rowDisableProp && row.original[rowDisableProp.key] === rowDisableProp.value) {
              return (
                <div className="checkbox-wrapper">
                  <Checkbox disabled={true} />
                </div>
              )
            }
            return (
              <IndeterminateCheckbox
                {...row.getToggleRowSelectedProps()}
                // totalCount={totalCount}
                rowData={row.original}
                selectedData={selectedData}
                maxSelect={maxSelect}
                uniqueKey={uniqueKey}
                title={`select row ${row.index + 1}`}
                bulkSelectionLimitMessage={bulkSelectionLimitMessage}
              />
            )
          }
          return <div className="no-checkbox-space"></div>
        } else {
          if (rowDisableProp) {
            if (row.original[rowDisableProp.key] === rowDisableProp.value) {
              return (
                <div className="checkbox-wrapper">
                  <Checkbox disabled={true} />
                </div>
              )
            }
          }
          return (
            <IndeterminateCheckbox
              {...row.getToggleRowSelectedProps()}
              // totalCount={totalCount}
              rowData={row.original}
              selectedData={selectedData}
              maxSelect={maxSelect}
              uniqueKey={uniqueKey}
              title={`select row ${row.index + 1}`}
              bulkSelectionLimitMessage={bulkSelectionLimitMessage}
            />
          )
        }
      }
    },
    ...columns
  ])
}


export const handleTableCustomHooks = (
  hooks,
  {
    isRowSelect,
    rowSelectCheckboxProp,
    selectedData,
    maxSelect,
    bulkSelectionLimitMessage,
    uniqueKey,
    rowDisableProp,
    v2Features,
    updateRowActionData,
    rowActionData,
    tableRowActionList,
    isResizable,
    columnWidthsObjectProp,
    tableWidth
  }
) => {
  if (isRowSelect) {
    pushCheckboxInRow(hooks, {
      rowSelectCheckboxProp,
      selectedData,
      maxSelect,
      uniqueKey,
      rowDisableProp,
      bulkSelectionLimitMessage,
    })
  }
  if (v2Features.tableRowAction) {
    pushActionColumn(hooks, {
      updateRowActionData,
      rowActionData,
      tableRowActionList
    })
  }
  if (isResizable) {
    //setting column Resize Width from taking from props
    hooks.useInstanceBeforeDimensions.push(({ headerGroups }) => {
      const headerGroupColumns = headerGroups[0]
      const columnWidthsArrayProp = Object.keys(columnWidthsObjectProp)
      headerGroupColumns.headers.map((headerGroup) => {
        columnWidthsArrayProp.forEach((key, index) => {
          if (headerGroup.id == key) {
            headerGroup.width = setWidth(columnWidthsObjectProp[key], tableWidth)
          }
        })
        headerGroup.canResize = !headerGroup.disableResizing
      })
    })
  }
}